import { useState } from 'react';
import { createRoot } from 'react-dom/client';
import { Calendar, User, CreditCard, CheckCircle2, XCircle, Clock, FileText, ChevronUp, ChevronDown, Search } from 'lucide-react';
import '../components/PendingAppointment.css'; // Re-using the same CSS file for a consistent look

// Main App component that renders the PendingAppointment page.
const App = () => {
  return (
    <div className="bg-container">
      <PendingAppointment />
    </div>
  );
};

// PendingAppointment component to display the list of pending bookings.
const PendingAppointment = () => {
  // Sample data for pending appointments
  const [appointments, setAppointments] = useState([
    {
      id: 1,
      bookingNo: "BK202507",
      date: "2025-08-17 09:00 AM",
      patient: "Robert Johnson",
      type: "Check-up",
      description: "Pre-surgery consultation.",
      amount: "₹2000",
      paymentStatus: "Pending",
      bookingStatus: "Pending",
    },
    // {
    //   id: 2,
    //   bookingNo: "BK202508",
    //   date: "2025-08-17 11:00 AM",
    //   patient: "Jessica Lee",
    //   type: "Dental",
    //   description: "Wisdom tooth extraction.",
    //   amount: "₹5500",
    //   paymentStatus: "Paid",
    //   bookingStatus: "Pending",
    // },
    {
      id: 2,
      bookingNo: "BK202509",
      date: "2025-08-18 02:30 PM",
      patient: "Chris Evans",
      type: "Physiotherapy",
      description: "Follow-up for knee injury.",
      amount: "₹1200",
      paymentStatus: "Pending",
      bookingStatus: "Pending",
    },
    {
      id: 3,
      bookingNo: "BK202510",
      date: "2025-08-19 10:00 AM",
      patient: "Laura Wilson",
      type: "Consultation",
      description: "General health consultation.",
      amount: "₹900",
      paymentStatus: "Pending",
      bookingStatus: "Pending",
    },
    
 
  ]);

  const [searchQuery, setSearchQuery] = useState('');
  const [showEntries, setShowEntries] = useState(10);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  // Handle search input changes
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  // Handle entries per page dropdown change
  const handleShowEntriesChange = (e) => {
    setShowEntries(Number(e.target.value));
  };

  // Logic to filter and sort appointments
  const filteredAppointments = appointments.filter(appt =>
    Object.values(appt).some(value =>
      String(value).toLowerCase().includes(searchQuery.toLowerCase())
    )
  );

  const sortedAppointments = [...filteredAppointments].sort((a, b) => {
    if (sortConfig.key === null) return 0;
    const aValue = a[sortConfig.key];
    const bValue = b[sortConfig.key];

    if (aValue < bValue) {
      return sortConfig.direction === 'asc' ? -1 : 1;
    }
    if (aValue > bValue) {
      return sortConfig.direction === 'asc' ? 1 : -1;
    }
    return 0;
  });

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return (
        <>
          <ChevronUp size={12} className="sort-icon-default" />
          <ChevronDown size={12} className="sort-icon-default" />
        </>
      );
    }
    if (sortConfig.direction === 'asc') {
      return <ChevronUp size={16} className="sort-icon-active" />;
    }
    return <ChevronDown size={16} className="sort-icon-active" />;
  };

  return (
    <div className="main-container ml-desktop">
      {/* Header with Title and Search/Entries controls */}
      <div className="header-section">
        <h1 className="main-title">Pending Appointment - List</h1>
        <div className="controls-group">
          <div className="entries-control">
            <span>Show</span>
            <select
              value={showEntries}
              onChange={handleShowEntriesChange}
              className="entries-select"
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            <span>entries</span>
          </div>
          <div className="search-control">
            <input
              type="text"
              placeholder="Search..."
              value={searchQuery}
              onChange={handleSearchChange}
              className="search-input"
            />
            <Search size={18} className="search-icon" />
          </div>
        </div>
      </div>

      {/* Desktop Table View */}
      <div className="table-container desktop-only">
        <table className="appointments-table">
          <thead>
            <tr>
              {['S.No', 'Booking No', 'Date / Time', 'Patient Name', 'Type', 'Description', 'Amount', 'Payment Status', 'Booking Status', 'Action'].map(header => (
                <th
                  key={header}
                  scope="col"
                  className="table-header-cell"
                  onClick={() => requestSort(header.split(' ').map((s, i) => i === 0 ? s.toLowerCase() : s.charAt(0).toUpperCase() + s.slice(1)).join(''))}
                >
                  <div className="sortable-header">
                    {header}
                    <span className="sort-icon-container">
                      {getSortIcon(header.split(' ').map((s, i) => i === 0 ? s.toLowerCase() : s.charAt(0).toUpperCase() + s.slice(1)).join(''))}
                    </span>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedAppointments.length > 0 ? (
              sortedAppointments.slice(0, showEntries).map((appt, index) => (
                <tr key={appt.id} className="table-row">
                  <td className="table-cell">{index + 1}</td>
                  <td className="table-cell font-semibold">{appt.bookingNo}</td>
                  <td className="table-cell">
                    <div className="cell-icon-text">
                      <Calendar size={16} className="icon-main-color" />
                      {appt.date}
                    </div>
                  </td>
                  <td className="table-cell">
                    <div className="cell-icon-text">
                      <User size={16} className="icon-main-color" />
                      {appt.patient}
                    </div>
                  </td>
                  <td className="table-cell">{appt.type}</td>
                  <td className="table-cell">{appt.description}</td>
                  <td className="table-cell font-semibold">{appt.amount}</td>
                  <td className="table-cell">
                    <span className={`badge ${appt.paymentStatus === "Paid" ? 'badge-paid' : 'badge-refunded'}`}>
                      {appt.paymentStatus === "Paid" ? <CheckCircle2 size={12} className="badge-icon" /> : <XCircle size={12} className="badge-icon" />}
                      {appt.paymentStatus}
                    </span>
                  </td>
                  <td className="table-cell">
                    <span className={`badge badge-pending`}>
                      <Clock size={12} className="badge-icon" />
                      {appt.bookingStatus}
                    </span>
                  </td>
                  <td className="table-cell action-cell">
                    <div className="action-buttons">
                      <button className="action-button">
                        <FileText size={18} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="10" className="table-cell text-center-none">
                  No pending appointments found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile Card View */}
      <div className="card-container mobile-only">
        {sortedAppointments.length > 0 ? (
          sortedAppointments.slice(0, showEntries).map((appt) => (
            <div key={appt.id} className="appointment-card">
              <div className="card-header">
                <h3 className="card-title">{appt.patient}</h3>
                <span className="card-subtitle">{appt.bookingNo}</span>
              </div>
              <div className="card-body">
                <div className="card-item">
                  <Calendar size={16} className="icon-main-color" />
                  <span>{appt.date}</span>
                </div>
                <div className="card-item">
                  <CreditCard size={16} className="icon-main-color" />
                  <span>{appt.amount}</span>
                </div>
                <div className="card-item">
                  <User size={16} className="icon-main-color" />
                  <span>{appt.type}</span>
                </div>
                <div className="card-item">
                  <FileText size={16} className="icon-main-color" />
                  <span>{appt.description}</span>
                </div>
              </div>
              <div className="card-footer">
                <span className={`badge ${appt.paymentStatus === "Paid" ? 'badge-paid' : 'badge-refunded'}`}>
                  {appt.paymentStatus === "Paid" ? <CheckCircle2 size={12} className="badge-icon" /> : <XCircle size={12} className="badge-icon" />}
                  {appt.paymentStatus}
                </span>
                <span className={`badge badge-pending`}>
                  <Clock size={12} className="badge-icon" />
                  {appt.bookingStatus}
                </span>
              </div>
            </div>
          ))
        ) : (
          <div className="no-results-message">
            No pending appointments found.
          </div>
        )}
      </div>
    </div>
  );
};

// Initial setup to render the React app
export default App;